import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addresses } from "@/lib/endpoints";
import { useApplicant } from "@/hooks/useApplicant";
import { useFormResponses } from "@/hooks/useFormResponses";
import { Field, TextInput, YesNo } from "@/components/Field";
import type {
  AddressListResponse,
  AddressResponse,
  CreateAddressRequest,
} from "@/lib/api-types";
import { getFieldErrors, getFormError } from "@/lib/validation-errors";

// form_responses keys for case-level mailing preferences. Names must
// match what the form mapping (n400_2025_01_20.go) reads.
const FR_KEYS = {
  currentInCareOf: "current_address_in_care_of",
  mailingSame: "current_mailing_same_as_physical",
  mailingInCareOf: "mailing_address_in_care_of",
} as const;

/**
 * Step 2 — Addresses (Part 3 of N-400).
 *
 * USCIS requires every address you've lived at over the past 5 years.
 *
 * Backend shape (services/api/internal/cases/nested.go::AddressRequest):
 *   street_1, street_2, city, state, postal_code, country, start_date,
 *   end_date, is_mailing, is_physical
 *
 * Whether an address is "current" or "previous" is derived from end_date
 * being null — there's no separate flag. is_mailing / is_physical describe
 * the role: PO-box-style mailing addresses are mailing-only.
 */
export function StepAddresses() {
  const { id } = useParams<{ id: string }>();
  const { applicant, isLoading: applicantLoading } = useApplicant(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const listKey = ["case", id, "person", applicant?.id, "addresses"] as const;

  const list = useQuery({
    queryKey: listKey,
    queryFn: () => addresses.list(id!, applicant!.id),
    enabled: !!id && !!applicant,
  });

  const upsertCache = (next: AddressResponse) => {
    qc.setQueryData<AddressListResponse>(listKey, (prev) => {
      if (!prev) return { items: [next], total: 1 };
      const idx = prev.items.findIndex((a) => a.id === next.id);
      if (idx === -1) return { items: [...prev.items, next], total: prev.total + 1 };
      const items = [...prev.items];
      items[idx] = next;
      return { ...prev, items };
    });
    qc.invalidateQueries({ queryKey: ["case", id, "evaluation"] });
  };

  const create = useMutation({
    mutationFn: (req: CreateAddressRequest) =>
      addresses.create(id!, applicant!.id, req),
    onSuccess: upsertCache,
  });

  const update = useMutation({
    mutationFn: ({ aid, req }: { aid: string; req: CreateAddressRequest }) =>
      addresses.update(id!, applicant!.id, aid, req),
    onSuccess: upsertCache,
  });

  const remove = useMutation({
    mutationFn: (aid: string) => addresses.delete(id!, applicant!.id, aid),
    onSuccess: (_, aid) => {
      qc.setQueryData<AddressListResponse>(listKey, (prev) =>
        prev
          ? {
              items: prev.items.filter((a) => a.id !== aid),
              total: prev.total - 1,
            }
          : prev,
      );
      qc.invalidateQueries({ queryKey: ["case", id, "evaluation"] });
    },
  });

  const [editing, setEditing] = useState<AddressResponse | "new" | null>(null);

  // Case-level mailing preferences (form_responses).
  const fr = useFormResponses(id);
  const [currentInCareOf, setCurrentInCareOf] = useState("");
  const [mailingSame, setMailingSame] = useState<boolean | null>(null);
  const [validationErr, setValidationErr] = useState<string | null>(null);
  const [mailingInCareOf, setMailingInCareOf] = useState("");

  useEffect(() => {
    if (fr.isLoading) return;
    setCurrentInCareOf(fr.getString(FR_KEYS.currentInCareOf));
    const ms = fr.get<boolean>(FR_KEYS.mailingSame);
    setMailingSame(typeof ms === "boolean" ? ms : null);
    setMailingInCareOf(fr.getString(FR_KEYS.mailingInCareOf));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, ...Object.values(FR_KEYS).map((k) => fr.get(k))]);

  if (applicantLoading || list.isLoading) {
    return <div className="text-slate-500">Loading…</div>;
  }

  const items = list.data?.items ?? [];
  // "Current physical home" = a physical address with no end_date.
  const hasCurrentPhysical = items.some(
    (a) => a.is_physical && !a.end_date,
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Addresses</h1>
        <p className="text-sm text-slate-500 mt-1">
          USCIS needs every address you&apos;ve lived at in the past 5 years.
          At least one entry must be your current physical home (a physical
          address with no end date).
        </p>
      </header>

      <ul className="space-y-3">
        {items.map((a) => (
          <li
            key={a.id}
            className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between"
          >
            <div className="text-sm">
              <div className="font-medium">
                {a.street_1}
                {a.street_2 ? `, ${a.street_2}` : ""}
              </div>
              <div className="text-slate-500">
                {[a.city, a.state, a.postal_code, a.country].filter(Boolean).join(", ")}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {a.is_physical ? "Physical" : ""}
                {a.is_physical && a.is_mailing ? " · " : ""}
                {a.is_mailing ? "Mailing" : ""}
                {a.start_date ? ` · from ${a.start_date}` : ""}
                {a.end_date ? ` to ${a.end_date}` : a.is_physical ? " · current" : ""}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setEditing(a)}
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm("Delete this address?")) remove.mutate(a.id);
                }}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {!editing && (
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          + Add address
        </button>
      )}

      {editing && (
        <AddressForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSubmit={async (req) => {
            if (editing === "new") {
              await create.mutateAsync(req);
            } else {
              await update.mutateAsync({ aid: editing.id, req });
            }
            setEditing(null);
          }}
          saving={create.isPending || update.isPending}
          /* Pass through whichever mutation is the active source of
             the error — Create when adding a new address, Update when
             editing an existing row. Only one fires at a time. */
          error={editing === "new" ? create.error : update.error}
        />
      )}

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setValidationErr(null);
          if (mailingSame === null) {
            setValidationErr("Please answer whether your mailing address is the same as physical.");
            return;
          }
          // Persist mailing-preference form_responses before navigating.
          const upserts: Promise<unknown>[] = [];
          if (currentInCareOf) {
            upserts.push(fr.upsert.mutateAsync({
              fieldKey: FR_KEYS.currentInCareOf, value: currentInCareOf,
            }));
          }
          upserts.push(fr.upsert.mutateAsync({
            fieldKey: FR_KEYS.mailingSame, value: mailingSame,
          }));
          if (mailingSame === false && mailingInCareOf) {
            upserts.push(fr.upsert.mutateAsync({
              fieldKey: FR_KEYS.mailingInCareOf, value: mailingInCareOf,
            }));
          }
          await Promise.all(upserts);
          navigate(`/cases/${id}/wizard/employment`);
        }}
        className="space-y-6"
      >
      {hasCurrentPhysical && (
        <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
          <legend className="font-medium text-slate-900 px-1">
            Mailing preferences
          </legend>
          <Field
            label='"In care of" name for your current address'
            hint="Only fill if mail at this address comes to you via someone else (e.g. a host family). Leave blank otherwise."
          >
            <TextInput
              value={currentInCareOf}
              onChange={(e) => setCurrentInCareOf(e.target.value)}
            />
          </Field>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Is your mailing address the same as your physical address?
            </p>
            <YesNo
              value={mailingSame}
              onChange={setMailingSame}
              labels={{ yes: "Yes, same", no: "No, different" }}
            />
          </div>
          {mailingSame === false && (
            <>
              <p className="text-xs text-slate-500">
                Add the separate mailing address above (mark it
                "Mailing" but not "Physical").
              </p>
              <Field
                label='"In care of" name for your mailing address'
                hint="Only fill if your mail comes via someone else (e.g. an attorney's office)."
              >
                <TextInput
                  value={mailingInCareOf}
                  onChange={(e) => setMailingInCareOf(e.target.value)}
                />
              </Field>
            </>
          )}
        </fieldset>
      )}

      {validationErr && (
        <p className="text-sm text-red-600">{validationErr}</p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link
          to={`/cases/${id}/wizard/biographic`}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Biographic info
        </Link>
        <button
          type="submit"
          disabled={!hasCurrentPhysical || fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
          title={hasCurrentPhysical ? "" : "Add a physical address with no end date to continue"}
        >
          {fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------

function AddressForm({
  initial,
  onCancel,
  onSubmit,
  saving,
  error,
}: {
  initial: AddressResponse | null;
  onCancel: () => void;
  onSubmit: (req: CreateAddressRequest) => Promise<void>;
  saving: boolean;
  error?: unknown;
}) {
  const [form, setForm] = useState<CreateAddressRequest>({
    street_1: initial?.street_1 ?? "",
    street_2: initial?.street_2 ?? "",
    city: initial?.city ?? "",
    state: initial?.state ?? "",
    postal_code: initial?.postal_code ?? "",
    // USCIS prints the country name verbatim on the PDF — default
    // to the readable form, not an ISO code.
    country: initial?.country ?? "United States",
    start_date: initial?.start_date ?? "",
    end_date: initial?.end_date ?? "",
    is_physical: initial?.is_physical ?? true,
    is_mailing: initial?.is_mailing ?? true,
  });

  const fieldErrors = getFieldErrors(error);
  const formError = getFormError(error);

  const set = <K extends keyof CreateAddressRequest>(
    key: K,
    value: CreateAddressRequest[K],
  ) => setForm((s) => ({ ...s, [key]: value }));

  const [isCurrent, setIsCurrent] = useState<boolean>(!initial?.end_date);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        // Empty strings → undefined so we don't clobber NULLable columns.
        const clean: CreateAddressRequest = {
          ...form,
          // Force end_date empty when "current" is checked.
          end_date: isCurrent ? "" : form.end_date ?? "",
        };
        (Object.keys(clean) as (keyof CreateAddressRequest)[]).forEach((k) => {
          const v = clean[k];
          if (typeof v === "string" && v === "") {
            (clean as Record<string, unknown>)[k] = undefined;
          }
        });
        void onSubmit(clean);
      }}
      className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4"
    >
      <h3 className="font-semibold">{initial ? "Edit address" : "Add address"}</h3>

      <fieldset className="text-sm">
        <legend className="font-medium">What kind of address is this?</legend>
        <div className="flex flex-wrap gap-4 mt-2 text-slate-700">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_physical}
              onChange={(e) => set("is_physical", e.target.checked)}
            />
            Physical (you live / lived here)
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_mailing}
              onChange={(e) => set("is_mailing", e.target.checked)}
            />
            Mailing (USCIS notices come here)
          </label>
        </div>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Street address"
          required
          className="sm:col-span-2"
          error={fieldErrors?.["street_1"]?.message}
        >
          <TextInput
            value={form.street_1 ?? ""}
            onChange={(e) => set("street_1", e.target.value)}
            autoComplete="address-line1"
          />
        </Field>
        <Field
          label="Apt / suite / unit"
          className="sm:col-span-2"
          error={fieldErrors?.["street_2"]?.message}
        >
          <TextInput
            value={form.street_2 ?? ""}
            onChange={(e) => set("street_2", e.target.value)}
            autoComplete="address-line2"
          />
        </Field>
        <Field label="City" required error={fieldErrors?.["city"]?.message}>
          <TextInput
            value={form.city ?? ""}
            onChange={(e) => set("city", e.target.value)}
            autoComplete="address-level2"
          />
        </Field>
        <Field
          label="State / province"
          required
          error={fieldErrors?.["state"]?.message}
        >
          <TextInput
            value={form.state ?? ""}
            onChange={(e) => set("state", e.target.value)}
            autoComplete="address-level1"
          />
        </Field>
        <Field
          label="ZIP / postal code"
          required
          error={fieldErrors?.["postal_code"]?.message}
        >
          <TextInput
            value={form.postal_code ?? ""}
            onChange={(e) => set("postal_code", e.target.value)}
            autoComplete="postal-code"
          />
        </Field>
        <Field
          label="Country"
          required
          error={fieldErrors?.["country"]?.message}
        >
          <TextInput
            value={form.country ?? ""}
            onChange={(e) => set("country", e.target.value)}
            autoComplete="country-name"
          />
        </Field>
        <Field
          label="Lived here from"
          required
          error={fieldErrors?.["start_date"]?.message}
        >
          <TextInput
            type="date"
            value={form.start_date ?? ""}
            onChange={(e) => set("start_date", e.target.value)}
          />
        </Field>
        <Field
          label="Lived here until"
          required={!isCurrent}
          hint={isCurrent ? "Leave blank — this is your current address" : undefined}
          error={fieldErrors?.["end_date"]?.message}
        >
          <TextInput
            type="date"
            value={form.end_date ?? ""}
            onChange={(e) => {
              set("end_date", e.target.value);
              if (e.target.value) setIsCurrent(false);
            }}
            disabled={isCurrent}
          />
        </Field>
      </div>

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isCurrent}
          onChange={(e) => {
            setIsCurrent(e.target.checked);
            if (e.target.checked) set("end_date", "");
          }}
        />
        I still live here (current address)
      </label>

      {formError && (
        <p className="text-sm text-red-600">Could not save: {formError}</p>
      )}
      {fieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {saving ? "Saving…" : initial ? "Save changes" : "Add address"}
        </button>
      </div>
    </form>
  );
}

